/**
 * CostDashboard — costos y throughput de los últimos N días.
 *
 * Endpoint:
 *   GET /api/qa/metrics/cost-summary?days=X →
 *     { totals: { runs, tokens_in, tokens_out, cost_usd, duration_ms, pass, fail, errors },
 *       byDay:  [{ day, runs, cost_usd, tokens }] }
 *
 * Render:
 *   - 4 KPI cards: runs, tokens (in+out), USD, pass rate.
 *   - Tabla byDay con barra CSS (no recharts).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE } from '../config';
import './CostDashboard.css';

const DAY_PRESETS = [
  { value: 7,  label: '7 días' },
  { value: 30, label: '30 días' },
  { value: 90, label: '90 días' },
];

function fmtNum(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('es-AR');
}

function fmtCost(usd) {
  const n = Number(usd);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(4)}`;
}

function fmtCostShort(usd) {
  const n = Number(usd);
  if (!Number.isFinite(n)) return '—';
  if (n >= 100) return `$${n.toFixed(2)}`;
  if (n >= 10)  return `$${n.toFixed(3)}`;
  return `$${n.toFixed(4)}`;
}

function fmtDay(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch (_) { return String(value); }
}

export default function CostDashboard() {
  const [days,    setDays]    = useState(30);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/qa/metrics/cost-summary?days=${encodeURIComponent(days)}`);
      if (res.status === 401 || res.status === 403) {
        throw new Error('Sin permisos.');
      }
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setData(j);
    } catch (e) {
      setError(e.message || 'Error al cargar cost summary');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = data?.totals || {};
  const byDay  = Array.isArray(data?.byDay) ? data.byDay : [];

  const totalTokens = (Number(totals.tokens_in) || 0) + (Number(totals.tokens_out) || 0);
  const totalPF     = (Number(totals.pass) || 0) + (Number(totals.fail) || 0) + (Number(totals.errors) || 0);
  const passRate    = totalPF
    ? ((Number(totals.pass) || 0) / totalPF * 100).toFixed(1)
    : '—';

  const maxCost = useMemo(() => {
    return byDay.reduce((m, d) => Math.max(m, Number(d.cost_usd) || 0), 0);
  }, [byDay]);

  return (
    <div className="um-card">
      <div className="qa-cost-toolbar">
        <span style={{ fontSize: '0.82rem', color: '#374151', fontWeight: 600 }}>
          Periodo:
        </span>
        <div className="qa-days-buttons">
          {DAY_PRESETS.map(p => (
            <button
              key={p.value}
              className={days === p.value ? 'active' : ''}
              onClick={() => setDays(p.value)}
              disabled={loading}
              type="button"
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          className="um-btn um-btn-ghost"
          onClick={fetchData}
          disabled={loading}
          style={{ marginLeft: 'auto' }}
        >
          Refrescar
        </button>
      </div>

      {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}

      {loading ? (
        <div className="um-loading">Cargando dashboard...</div>
      ) : (
        <>
          <div className="qa-cost-kpis">
            <div className="qa-cost-kpi">
              <div className="qa-cost-kpi-label">Runs</div>
              <div className="qa-cost-kpi-value">{fmtNum(totals.runs)}</div>
              <div className="qa-cost-kpi-sub">en los últimos {days} días</div>
            </div>
            <div className="qa-cost-kpi">
              <div className="qa-cost-kpi-label">Tokens (in+out)</div>
              <div className="qa-cost-kpi-value">{fmtNum(totalTokens)}</div>
              <div className="qa-cost-kpi-sub">
                in: {fmtNum(totals.tokens_in)} / out: {fmtNum(totals.tokens_out)}
              </div>
            </div>
            <div className="qa-cost-kpi">
              <div className="qa-cost-kpi-label">USD</div>
              <div className="qa-cost-kpi-value">{fmtCost(totals.cost_usd)}</div>
              <div className="qa-cost-kpi-sub">
                {totals.runs ? `${fmtCost(Number(totals.cost_usd) / Number(totals.runs))} / run` : '—'}
              </div>
            </div>
            <div className="qa-cost-kpi">
              <div className="qa-cost-kpi-label">Pass rate</div>
              <div className="qa-cost-kpi-value">{passRate === '—' ? '—' : `${passRate}%`}</div>
              <div className="qa-cost-kpi-sub">
                {fmtNum(totals.pass)} pass / {fmtNum(totals.fail)} fail / {fmtNum(totals.errors)} err
              </div>
            </div>
          </div>

          <div className="qa-cost-section-title">Por día</div>

          <div className="um-table-wrap">
            {byDay.length === 0 ? (
              <div className="um-empty">Sin runs en este periodo.</div>
            ) : (
              <table className="qa-cost-table">
                <thead>
                  <tr>
                    <th>Día</th>
                    <th className="qa-cost-num">Runs</th>
                    <th className="qa-cost-num">Tokens</th>
                    <th className="qa-cost-num">Cost</th>
                    <th>Distribución cost</th>
                  </tr>
                </thead>
                <tbody>
                  {byDay.map(d => {
                    const pct = maxCost > 0 ? (Number(d.cost_usd) || 0) / maxCost * 100 : 0;
                    return (
                      <tr key={d.day}>
                        <td>{fmtDay(d.day)}</td>
                        <td className="qa-cost-num">{fmtNum(d.runs)}</td>
                        <td className="qa-cost-num">{fmtNum(d.tokens)}</td>
                        <td className="qa-cost-num">{fmtCostShort(d.cost_usd)}</td>
                        <td className="qa-cost-bar-cell">
                          <div className="qa-cost-bar-track">
                            <div className="qa-cost-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
